import {ServiceBase} from "../ServiceBase";
import {
    IAbTestingAiSortData,
    IAbTestingFeature,
    IAbTestingFeatureDataProviderService
} from "./ab-testing-feature-data-provider.service.interface";
import {
    abTestingFeatureIsValid,
    isAbFeaturePropertyValueTrue
} from "./ab-testing-feature-check";
import {
    FEATURE_CASHOUT_WIDGET,
    FEATURE_DEPOSIT_WIDGET,
    FEATURE_NAME_AI_SORT_GAME_LIST,
    FEATURE_NAME_CONTROL_CENTER_TOGGLE,
    FEATURE_NAME_FREE_TO_PLAY_FLOW,
    FEATURE_NAME_LIST_ON_TOP,
    FEATURE_NAME_MOBILE_HEADER_SEARCH_BUTTON,
    FEATURE_NAME_ONBOARDING_TOUR,
    FEATURE_NAME_PLAYERS_LIKE_YOU_GAMES_LIST_TYPE_ID,
    FEATURE_NAME_REGISTRATION_WIDGET,
    FEATURE_NAME_SEARCH_GAME_SUGGESTED_BY_AI,
    FEATURE_NAME_STATIC_VIDEO_LIST,
    FEATURE_NAME_STATIC_LIVE_ARENA,
    FEATURE_NAME_VIDEO_LIVE_STREAM,
    FEATURE_NAME_VIDEO_SLIDES,
    FEATURE_NAME_WEBAPP_BOTTOM_NAVIGATION,
    FEATURE_TRANSACTION_HISTORY_WIDGET, FEATURE_NAME_ADD_IN_GAME_FUNDS, FEATURE_NAME_JACKPOT_BET,
    FEATURE_NAME_TABS_NAVIGATION, FEATURE_NAME_ARENA_BANNERS_AB_TEST
} from "../../../common/const/ab-testing-names";
import {makeObservable, observable} from 'mobx';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {Check} from "@sparkware/app-utils";
import {FeatureToggleModel, FeatureToggleModelLazy, IFeatureToggleModelOptions} from "./feature-toggle.model";
import {FeatureToggleStatusEnum} from "./feature-toggle.view-model.interface";
import {IAbTestingStrategy} from "./strategies/ab-testing-strategy.interface";
import {VoidAbTestingStrategy} from "./strategies/void/void-ab-testing.strategy";
import {AbTestingForAuthorizedUserStrategy} from "./strategies/authorized/ab-testing-for-authorized-user.strategy";
import {AbTestingForAnonymousUserStrategy} from "./strategies/anonymous/ab-testing-for-anonymous-user.strategy";
import {NullableString} from "../../../common/types/nullable-types";

export class AbTestingFeatureDataProviderService extends ServiceBase implements IAbTestingFeatureDataProviderService {

    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this.reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                if (isAuthorized) {
                    this._currentAbTestingStrategy = new AbTestingForAuthorizedUserStrategy(this.services);
                } else {
                    this._currentAbTestingStrategy = new AbTestingForAnonymousUserStrategy(this.services);
                }
            }, {
            fireImmediately: true
        });
    }

    @observable.ref
    private _currentAbTestingStrategy: IAbTestingStrategy = new VoidAbTestingStrategy();

    get anonymousPlayerId(): NullableString {
        return this._currentAbTestingStrategy.anonymousPlayerId;
    }

    private async _getFeatureToggleAsync(featureToggleLazy: FeatureToggleModelLazy): Promise<FeatureToggleModel> {
        await this._currentAbTestingStrategy.waitForLoadTestData();
        return featureToggleLazy.value;
    }

    getFeature(featureName: string): IAbTestingFeature | null {
        return this._currentAbTestingStrategy.getFeature(featureName);
    }

    waitForLoadTestData(): Promise<void> {
        return this._currentAbTestingStrategy.waitForLoadTestData();
    }


    private _createFeatureToggle(abFeatureName: string, getToggleStatus: () => FeatureToggleStatusEnum, options?: IFeatureToggleModelOptions): FeatureToggleModel {
        return new FeatureToggleModel(abFeatureName, getToggleStatus, () => this._currentAbTestingStrategy, this.services, options);
    }


    private _createFeatureToggleLazy(abFeatureName: string, getToggleStatus: () => FeatureToggleStatusEnum, options?: IFeatureToggleModelOptions): FeatureToggleModelLazy {
        return new FeatureToggleModelLazy(() => {
            return this._createFeatureToggle(abFeatureName, getToggleStatus, options)
        })
    }

    private _depositWidget = this._createFeatureToggleLazy(FEATURE_DEPOSIT_WIDGET, () => this.services.configuration.servicesConfigs.AppLauncher.apps.PAYMENTS_DEPOSIT.featureToggle);
    get depositWidget(): FeatureToggleModel {
        return this._depositWidget.value;
    }

    private _cashOutWidget = this._createFeatureToggleLazy(FEATURE_CASHOUT_WIDGET, () => this.services.configuration.servicesConfigs.AppLauncher.apps.PAYMENTS_CASHOUT.featureToggle);
    get cashoutWidget(): FeatureToggleModel {
        return this._cashOutWidget.value;
    }

    private _transactionHistoryWidget = this._createFeatureToggleLazy(FEATURE_TRANSACTION_HISTORY_WIDGET, () => this.services.configuration.servicesConfigs.AppLauncher.apps.PAYMENTS_PLAYER_TRANSACTIONS_HISTORY.featureToggle);
    get transactionHistoryWidget(): FeatureToggleModel {
        return this._transactionHistoryWidget.value;
    }

    private _registrationWidget = this._createFeatureToggleLazy(FEATURE_NAME_REGISTRATION_WIDGET, () => this.services.configuration.servicesConfigs.AppLauncher.apps.FUNNEL_REGISTRATION.featureToggle);
    public async registrationWidgetAsync(): Promise<FeatureToggleModel> {
        return await this._getFeatureToggleAsync(this._registrationWidget);
    }

    private _controlCenter = this._createFeatureToggleLazy(FEATURE_NAME_CONTROL_CENTER_TOGGLE,
        () => this.services.configuration.servicesConfigs.AppLauncher.apps.RG_CONTROL_CENTER_FULL.featureToggle, {
        isFeatureEnabledByAB: (featureData: IAbTestingFeature) => isAbFeaturePropertyValueTrue(featureData, "enablesafezone"),
        getFeatureToggleStatusPerRegulation: () => this.services.configuration.servicesConfigs.AppLauncher.apps.RG_CONTROL_CENTER_FULL.featureToggleForRegulation
    });
    get controlCenter(): FeatureToggleModel {
        return this._controlCenter.value;
    }

    private _onboardingTour = this._createFeatureToggleLazy(FEATURE_NAME_ONBOARDING_TOUR,
        () => this.services.configuration.data.onboardingTour?.featureToggle ?? FeatureToggleStatusEnum.Disabled);
    get onboardingTour(): FeatureToggleModel {
        return this._onboardingTour.value;
    }

    private _videoLiveStream = this._createFeatureToggleLazy(FEATURE_NAME_VIDEO_LIVE_STREAM,
        () => FeatureToggleStatusEnum.EnabledByAB)
    get videoLiveStream(): FeatureToggleModel {
        return this._videoLiveStream.value;
    }

    private _staticVideoList = this._createFeatureToggleLazy(FEATURE_NAME_STATIC_VIDEO_LIST,
        () => FeatureToggleStatusEnum.EnabledByAB) /*So far this is a pure A/B only feature*/
    get staticVideoList(): FeatureToggleModel {
        return this._staticVideoList.value;
    }

    private _videoSlides = this._createFeatureToggleLazy(FEATURE_NAME_VIDEO_SLIDES,
        () => this.services.configuration.servicesConfigs.VideoSlides.featureToggle);
    get videoSlides(): FeatureToggleModel {
        return this._videoSlides.value;
    }

    private _freeToPlayFlow = this._createFeatureToggleLazy(FEATURE_NAME_FREE_TO_PLAY_FLOW,
        () => FeatureToggleStatusEnum.EnabledByAB);
    get freeToPlayFlow(): FeatureToggleModel {
        return this._freeToPlayFlow.value;
    }

    private _mobileHeaderSearchButton = this._createFeatureToggleLazy(FEATURE_NAME_MOBILE_HEADER_SEARCH_BUTTON, () => FeatureToggleStatusEnum.EnabledByAB);
    get mobileHeaderSearchButton(): FeatureToggleModel {
        return this._mobileHeaderSearchButton.value;
    }

    private _webappBottomNavigation = this._createFeatureToggleLazy(FEATURE_NAME_WEBAPP_BOTTOM_NAVIGATION,
        () => this.services.configuration.data.bottomNavigationBar?.webAppFeatureToggle ?? FeatureToggleStatusEnum.Disabled);
    get webappBottomNavigation(): FeatureToggleModel {
        return this._webappBottomNavigation.value;
    }

    private _addInGameFunds = this._createFeatureToggleLazy(FEATURE_NAME_ADD_IN_GAME_FUNDS,
        () => this.services.configuration.servicesConfigs.Game.enableAddInGameFunds);
    get addInGameFunds(): FeatureToggleModel {
        return this._addInGameFunds.value;
    }

    private _jackpotBet = this._createFeatureToggleLazy(FEATURE_NAME_JACKPOT_BET,
        () => FeatureToggleStatusEnum.EnabledByAB);
    get jackpotBet(): FeatureToggleModel {
        return this._jackpotBet.value;
    }

    get aiSortGamesLists(): IAbTestingAiSortData {

        const aiSortGamesListsFeatureData = this.getFeature(FEATURE_NAME_AI_SORT_GAME_LIST);

        const result: IAbTestingAiSortData = {arenas: []};
        if (abTestingFeatureIsValid(aiSortGamesListsFeatureData, ["arenas"]) && Check.isString(aiSortGamesListsFeatureData.Properties.arenas)) {
            try {
                result.arenas = JSON.parse(aiSortGamesListsFeatureData.Properties.arenas.replace(/'/g, '"'));
            } catch (err) {
                this.services.logger.error(`Failed to parse AB Test data for feature "${FEATURE_NAME_AI_SORT_GAME_LIST}"`, err);
            }
        }
        return result;
    }

    get playersLikeYouGamesListTypeId(): NullableString {
        const abTestingPlayersLikeYouGamesListTypeIdAIData = this.getFeature(FEATURE_NAME_PLAYERS_LIKE_YOU_GAMES_LIST_TYPE_ID);

        if (abTestingPlayersLikeYouGamesListTypeIdAIData) {
            if (abTestingFeatureIsValid(abTestingPlayersLikeYouGamesListTypeIdAIData, ["listid"])) {
                return abTestingPlayersLikeYouGamesListTypeIdAIData.Properties?.listid?.toLowerCase() ?? null;
            }
        }

        return null;
    }

    get searchGameSuggestionByAI(): string | null {
        const abTestingSearchGameSuggestionByAI = this.getFeature(FEATURE_NAME_SEARCH_GAME_SUGGESTED_BY_AI);
        if (abTestingSearchGameSuggestionByAI) {
            if (abTestingFeatureIsValid(abTestingSearchGameSuggestionByAI, ["aitype"])) {
                return abTestingSearchGameSuggestionByAI.Properties?.aitype?.toLowerCase();
            }
        }

        return null;
    }

    get listOnTop(): number[] | null {
        const abTestingListOnTopData = this.getFeature(FEATURE_NAME_LIST_ON_TOP);
        if (abTestingListOnTopData) {
            if (abTestingFeatureIsValid(abTestingListOnTopData, ["listid"])) {
                let listId = abTestingListOnTopData.Properties?.listid;
                const splidedList = listId.split(',');

                listId = splidedList.map((id: string) => parseInt(id));

                return listId
            }
        }

        return null;
    }

    private _staticLiveArena = this._createFeatureToggleLazy(FEATURE_NAME_STATIC_LIVE_ARENA,
        () => FeatureToggleStatusEnum.EnabledByAB);
    get staticLiveArena(): FeatureToggleModel {
        return this._staticLiveArena.value;
    }

    private _siteNewNavigation = this._createFeatureToggleLazy(FEATURE_NAME_TABS_NAVIGATION,
        () => this.services.configuration.servicesConfigs.SiteNewNavigation?.featureToggle);
    get siteNewNavigation(): FeatureToggleModel {
        return this._siteNewNavigation.value;
    }

    private _arenaBannerTest = this._createFeatureToggleLazy(FEATURE_NAME_ARENA_BANNERS_AB_TEST,
        () => this.services.configuration.servicesConfigs.ArenaBannerTest?.featureToggle ?? FeatureToggleStatusEnum.Disabled);
    get arenaBannerTest(): FeatureToggleModel {
        return this._arenaBannerTest.value;
    }
}
